import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import Select from '@atlaskit/select';

import { BOOLEAN_FIELD, FIELD_MAPPINGS, OPERATIONS } from '../../../../common/constants';
import { ConditionFilterMessages } from '../../../../common/messages';
import { type IConditionFieldProps } from '../../../../common/types';
import { Textfield } from '../../../../common/ui/styled';

const KeyValueConditionFields: React.FC<IConditionFieldProps> = ({
	field,
	fieldType,
	update,
	operation: op,
	not,
	expectedValue,
	isFieldKeyRequired,
	isDisabled,
	isReadOnly,
}) => {
	const { formatMessage } = useIntl();

	const { availableOperations, operation } = useMemo(() => {
		const availableOperations = FIELD_MAPPINGS[fieldType].operations;
		const operation = OPERATIONS[op] ?? FIELD_MAPPINGS[fieldType].default;
		return { availableOperations, operation };
	}, [fieldType, op]);

	const bool = useMemo(() => BOOLEAN_FIELD.find((b) => b.value === not), [not]);

	useEffect(() => {
		if (!OPERATIONS[op]) {
			update({ operation: FIELD_MAPPINGS[fieldType].default.value });
		}
	}, [fieldType, op, update]);

	return (
		<>
			<Textfield
				placeholder={formatMessage(ConditionFilterMessages.KeyValuePlaceholder)}
				name="field"
				value={field}
				onChange={(e) => update({ field: e.currentTarget.value })}
				isRequired={isFieldKeyRequired}
				isDisabled={isDisabled}
				isReadOnly={isReadOnly}
				aria-label={formatMessage(ConditionFilterMessages.EnterKeyFieldAriaLabel)}
			/>
			<Select
				value={bool}
				onChange={(boolValue) => boolValue && update({ not: boolValue.value })}
				options={BOOLEAN_FIELD}
				isDisabled={isDisabled}
				menuIsOpen={isReadOnly ? false : undefined}
				isSearchable={!isReadOnly}
				menuPosition="fixed"
				label={formatMessage(ConditionFilterMessages.IsNotFieldAriaLabel)}
			/>
			<Select
				value={operation}
				options={availableOperations}
				isDisabled={isDisabled}
				menuIsOpen={isReadOnly ? false : undefined}
				isSearchable={!isReadOnly}
				onChange={(op) => {
					if (!op) {
						return;
					}

					if (op.value !== OPERATIONS['is-empty'].value) {
						update({ operation: op.value });
					} else {
						update({ operation: op.value, expectedValue: '' });
					}
				}}
				menuPosition="fixed"
				label={formatMessage(ConditionFilterMessages.ValueFieldAriaLabel)}
			/>
			{operation.value !== OPERATIONS['is-empty'].value && (
				<Textfield
					name="expectedValue"
					placeholder={formatMessage(ConditionFilterMessages.ExpectedValuePlaceholder)}
					value={expectedValue}
					isDisabled={isDisabled}
					isReadOnly={isReadOnly}
					onChange={(e) => update({ expectedValue: e.currentTarget.value })}
					aria-label={formatMessage(ConditionFilterMessages.EnterValueFieldAriaLabel)}
				/>
			)}
		</>
	);
};

export default KeyValueConditionFields;
